import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
  EventEmitter,
  Output,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { NgStyle, NgClass } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';

export interface IStep {
  label: string;
  completed: boolean;
}

@Component({
  selector: 'ag1-stepper',
  templateUrl: './ag1-stepper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle, NgClass, AngularSvgIconModule],
})
export class Ag1StepperComponent implements OnInit, AfterViewInit {
  @Input() orientation: 'horizontal' | 'vertical' = 'horizontal';

  @Input() steps: IStep[] = [];
  @Input() activeStep: IStep;

  @Output() activeStepChange = new EventEmitter<IStep>();

  // grab step elements in order to scroll to them
  @ViewChildren('stepElement') stepElements: QueryList<ElementRef>;

  constructor() {}

  ngOnInit(): void {
    if (!this.steps?.length) {
      console.error('Steps are required', this.steps);
    }
  }

  ngAfterViewInit(): void {
    // scroll to the active step after the DOM has been loaded
    const index: number = this.steps.findIndex(
      (step: IStep) => step.label === this.activeStep.label,
    );

    this.scrollToStep(index);
  }

  activateStep(step: IStep): void {
    // only allow activation if the step is completed
    if (!step.completed) {
      return;
    }

    this.activeStep = step;
    this.activeStepChange.emit(this.activeStep);

    const index: number = this.steps.findIndex(
      (step: IStep) => step.label === this.activeStep.label,
    );

    // set timeout to ensure that scrolling only happens after the DOM has been updated
    setTimeout(() => {
      this.scrollToStep(index);
    }, 50);
  }

  nextStep(): void {
    this.completeStep(this.activeStep);

    const index: number = this.steps.findIndex(
      (step: IStep) => step.label === this.activeStep.label,
    );
    this.activeStep = this.steps[index + 1];
    this.activeStepChange.emit(this.activeStep);

    setTimeout(() => {
      this.scrollToStep(index + 1);
    }, 50);
  }

  scrollToStep(index: number): void {
    const elements: ElementRef[] = this.stepElements.toArray();
    elements[index]?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  isActiveStep(step: IStep): boolean {
    return step?.label === this.activeStep?.label;
  }

  isCompletedStep(step: IStep): boolean {
    return step?.completed;
  }

  completeStep(step: IStep): void {
    step.completed = true;
  }

  get isVertical(): boolean {
    return this.orientation === 'vertical';
  }

  get isHorizontal(): boolean {
    return this.orientation === 'horizontal';
  }
}
